/**
 * Authentification côté client : appels à l'API HTTP et conservation du jeton
 * de session (localStorage). Le jeton n'est jamais mis dans une URL.
 */
export interface Account {
  id: string;
  name: string;
  kind: 'guest' | 'user';
}

const KEY = 'snowchain.session';

export function savedSession(): { token: string; account: Account } | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as { token: string; account: Account }) : null;
  } catch {
    return null;
  }
}

function save(s: { token: string; account: Account }): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body ?? {}),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Erreur ${res.status}`);
  return data;
}

export async function playAsGuest(name?: string): Promise<{ token: string; account: Account }> {
  const s = await post<{ token: string; account: Account }>('/api/auth/guest', name ? { name } : {});
  save(s);
  return s;
}

export async function login(name: string, password: string): Promise<{ token: string; account: Account }> {
  const s = await post<{ token: string; account: Account }>('/api/auth/login', { name, password });
  save(s);
  return s;
}

export async function register(name: string, password: string): Promise<{ token: string; account: Account }> {
  const s = await post<{ token: string; account: Account }>('/api/auth/register', { name, password });
  save(s);
  return s;
}

/** Transforme la session invitée courante en compte avec mot de passe. */
export async function claim(token: string, name: string, password: string): Promise<Account> {
  const r = await post<{ account: Account }>('/api/auth/claim', { name, password }, token);
  save({ token, account: r.account });
  return r.account;
}

export async function checkSession(token: string): Promise<Account | null> {
  const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  return ((await res.json()) as { account: Account }).account;
}

export async function logout(token: string): Promise<void> {
  await post('/api/auth/logout', {}, token).catch(() => undefined);
  clearSession();
}
